import React, { useState, useEffect } from 'react';
import { Box, Typography, Chip, alpha, keyframes, useTheme } from '@mui/material';
import {
  Stadium as VenueIcon,
  FiberManualRecord as DotIcon,
  SportsFootball as FootballIcon,
  SportsBasketball as BasketballIcon,
  SportsSoccer as SoccerIcon,
  SportsHockey as HockeyIcon,
  AccessTime as ClockIcon,
} from '@mui/icons-material';
import { sportsApi } from '../../api';
import type { ExternalGame, TeamMapping } from '../../types';
import { decomposeDuration } from '../../utils/dateFormatting';
import { TeamLogo } from '../shared/TeamLogo';

interface LiveGameBannerProps {
  externalGameId: string;
  sport: string;
}

const POLL_INTERVAL = 30_000;

const sportIconMap: Record<string, React.ReactNode> = {
  football: <FootballIcon sx={{ fontSize: 16 }} />,
  basketball: <BasketballIcon sx={{ fontSize: 16 }} />,
  soccer: <SoccerIcon sx={{ fontSize: 16 }} />,
  hockey: <HockeyIcon sx={{ fontSize: 16 }} />,
};

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
`;

const tickFlash = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.25; }
  100% { opacity: 1; }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const reveal = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
`;

function getQuarterLabel(sport: string, quarter: number | null): string {
  if (quarter == null) return '';
  if (sport === 'hockey') return `P${quarter}`;
  if (sport === 'soccer') return quarter === 1 ? '1st Half' : '2nd Half';
  return `Q${quarter}`;
}

interface CountdownData {
  segments: { value: string; unit: string }[];
  isUrgent: boolean;
  isImminent: boolean;
  expired: boolean;
}

function formatCountdown(diffMs: number): CountdownData {
  if (diffMs <= 0) return { segments: [], isUrgent: true, isImminent: true, expired: true };

  const { days, hours, minutes, seconds } = decomposeDuration(diffMs);
  const totalSeconds = Math.floor(diffMs / 1000);

  const segments: { value: string; unit: string }[] = [];
  if (days > 0) segments.push({ value: String(days).padStart(2, '0'), unit: 'DAY' });
  if (hours > 0 || days > 0) segments.push({ value: String(hours).padStart(2, '0'), unit: 'HR' });
  segments.push({ value: String(minutes).padStart(2, '0'), unit: 'MIN' });
  if (days === 0) segments.push({ value: String(seconds).padStart(2, '0'), unit: 'SEC' });

  return {
    segments,
    isUrgent: totalSeconds < 3600,
    isImminent: totalSeconds < 300,
    expired: false,
  };
}

function getStatusColor(game: ExternalGame): string {
  switch (game.status) {
    case 'in_progress': return '#ef4444';
    case 'final': return '#6b7280';
    case 'postponed': return '#f59e0b';
    case 'canceled': return '#ef4444';
    default: {
      const diffMs = new Date(game.gameDate).getTime() - Date.now();
      if (diffMs < 0) return '#f59e0b';
      if (diffMs < 3600000) return '#f59e0b';
      return '#509ed3';
    }
  }
}

/** Countdown digit tile — light elevated card that matches the site theme */
function DigitCell({ value, isUrgent, primaryColor }: { value: string; isUrgent: boolean; primaryColor: string }) {
  return (
    <Box
      sx={{
        bgcolor: '#fff',
        borderRadius: '8px',
        px: { xs: 0.9, sm: 1.3 },
        py: { xs: 0.5, sm: 0.7 },
        minWidth: { xs: 30, sm: 38 },
        textAlign: 'center',
        position: 'relative',
        boxShadow: isUrgent
          ? `0 2px 8px ${alpha('#f59e0b', 0.2)}, 0 0 0 1px ${alpha('#f59e0b', 0.25)}`
          : `0 1px 4px ${alpha('#000', 0.08)}, 0 0 0 1px ${alpha(primaryColor, 0.12)}`,
        transition: 'box-shadow 0.3s ease',
      }}
    >
      <Typography
        component="span"
        sx={{
          fontFamily: '"DM Mono", "Menlo", monospace',
          fontSize: { xs: '1.2rem', sm: '1.6rem' },
          fontWeight: 500,
          color: isUrgent ? '#d97706' : '#2D3748',
          lineHeight: 1,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '0.02em',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

/** Colon between digit groups */
function ColonSeparator({ blink, primaryColor }: { blink: boolean; primaryColor: string }) {
  return (
    <Typography
      component="span"
      sx={{
        fontFamily: '"DM Mono", "Menlo", monospace',
        fontSize: { xs: '1rem', sm: '1.3rem' },
        fontWeight: 500,
        color: alpha(primaryColor, 0.4),
        lineHeight: 1,
        mx: 0.2,
        ...(blink && {
          animation: `${tickFlash} 1s ease-in-out infinite`,
        }),
      }}
    >
      :
    </Typography>
  );
}

export const LiveGameBanner: React.FC<LiveGameBannerProps> = ({ externalGameId, sport }) => {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main; // #509ed3
  const [game, setGame] = useState<ExternalGame | null>(null);
  const [teams, setTeams] = useState<TeamMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState<CountdownData | null>(null);

  useEffect(() => {
    let mounted = true;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const fetchGame = async () => {
      try {
        const data = await sportsApi.getGame(externalGameId);
        if (!mounted) return;
        setGame(data);
        setTeams(data.teamMappings || []);

        if (data.status === 'in_progress' || data.status === 'scheduled') {
          if (!intervalId) {
            intervalId = setInterval(async () => {
              try {
                const updated = await sportsApi.getGame(externalGameId);
                if (mounted) {
                  setGame(updated);
                  setTeams(updated.teamMappings || []);
                  if (updated.status === 'final' || updated.status === 'canceled') {
                    if (intervalId) clearInterval(intervalId);
                    intervalId = null;
                  }
                }
              } catch { /* retry next interval */ }
            }, POLL_INTERVAL);
          }
        }
      } catch {
        // silently fail
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchGame();
    return () => {
      mounted = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [externalGameId]);

  // Countdown timer — uses a ref-stored gameDate to avoid stale closures
  const gameDateRef = React.useRef<string | null>(null);
  gameDateRef.current = game?.status === 'scheduled' ? game.gameDate : null;

  useEffect(() => {
    if (!game || game.status !== 'scheduled') {
      setCountdown(null);
      return;
    }
    const gameDate = game.gameDate;
    const tick = () => {
      const diffMs = new Date(gameDate).getTime() - Date.now();
      setCountdown(formatCountdown(diffMs));
    };
    tick();
    const tickId = setInterval(tick, 1000);
    return () => clearInterval(tickId);
  }, [game?.status, game?.gameDate]);

  // Don't render anything until data loads successfully — prevents flashing
  if (loading || !game) return null;

  const homeTeam = teams.find(t => t.externalTeamId === game.homeTeamId);
  const awayTeam = teams.find(t => t.externalTeamId === game.awayTeamId);
  const homeColor = homeTeam?.primaryColor || '#1976d2';
  const awayColor = awayTeam?.primaryColor || '#d32f2f';
  const statusColor = getStatusColor(game);
  const isLive = game.status === 'in_progress';
  const isScheduled = game.status === 'scheduled';
  const hasScore = game.homeScore != null && game.awayScore != null;

  let statusLabel = '';
  let statusPulse = false;
  if (isLive) {
    const qLabel = getQuarterLabel(sport, game.quarter);
    let time = '';
    if (game.timeRemaining) {
      const mins = parseFloat(game.timeRemaining);
      if (!isNaN(mins)) {
        const m = Math.floor(mins);
        const s = Math.round((mins - m) * 60);
        time = ` · ${m}:${s.toString().padStart(2, '0')}`;
      }
    }
    statusLabel = `LIVE${qLabel ? ` — ${qLabel}` : ''}${time}`;
    statusPulse = true;
  } else if (game.status === 'final') {
    statusLabel = 'FINAL';
  } else if (game.status === 'postponed') {
    statusLabel = 'POSTPONED';
  } else if (game.status === 'canceled') {
    statusLabel = 'CANCELED';
  } else if (countdown) {
    statusLabel = countdown.expired ? 'STARTING SOON' : 'COUNTDOWN';
    statusPulse = countdown.expired || countdown.isImminent;
  }

  const showCountdown = isScheduled && countdown && countdown.segments.length > 0;

  return (
    <Box
      sx={{
        mb: 2,
        borderRadius: 3,
        overflow: 'hidden',
        position: 'relative',
        bgcolor: '#fff',
        boxShadow: isLive
          ? `0 0 0 1px ${alpha(statusColor, 0.2)}, 0 4px 20px ${alpha(statusColor, 0.12)}`
          : '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
        animation: `${reveal} 0.4s ease-out`,
      }}
    >
      {/* Team color gradient stripe */}
      <Box
        sx={{
          height: 4,
          background: `linear-gradient(90deg, ${awayColor} 0%, ${alpha(awayColor, 0.15)} 40%, ${alpha(homeColor, 0.15)} 60%, ${homeColor} 100%)`,
        }}
      />

      {/* Main content */}
      <Box
        sx={{
          px: { xs: 1.5, sm: 2.5 },
          py: showCountdown ? { xs: 1.5, sm: 2 } : 1.5,
          background: showCountdown
            ? `linear-gradient(180deg, ${alpha(primaryColor, 0.03)} 0%, ${alpha('#F7FAFC', 0.8)} 100%)`
            : `linear-gradient(135deg, ${alpha(awayColor, 0.04)} 0%, transparent 50%, ${alpha(homeColor, 0.04)} 100%)`,
        }}
      >
        {/* ── Countdown mode ── */}
        {showCountdown && (
          <Box>
            {/* Top: status + venue */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 1.25, sm: 1.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ color: '#718096', display: 'flex' }}>
                  {sportIconMap[sport] || <VenueIcon sx={{ fontSize: 14 }} />}
                </Box>
                <Chip
                  label={statusLabel}
                  size="small"
                  sx={{
                    bgcolor: alpha(statusColor, 0.1),
                    color: statusColor,
                    fontWeight: 700,
                    fontSize: '0.6rem',
                    letterSpacing: '0.08em',
                    height: 20,
                    ...(statusPulse && { animation: `${pulse} 2s ease-in-out infinite` }),
                  }}
                  icon={statusPulse ? <DotIcon sx={{ fontSize: '8px !important', color: `${statusColor} !important` }} /> : undefined}
                />
              </Box>
              {game.venue && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#718096' }}>
                  <VenueIcon sx={{ fontSize: 12 }} />
                  <Typography sx={{ fontSize: '0.6rem', letterSpacing: '0.03em' }}>
                    {game.venue}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Matchup + countdown row */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: { xs: 1.5, sm: 3 },
              }}
            >
              {/* Away team */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 0.75, sm: 1.25 },
                  flex: '0 1 auto',
                  minWidth: 0,
                }}
              >
                <TeamLogo mapping={awayTeam} fallbackColor={awayColor} size={44} />
                <Typography
                  noWrap
                  sx={{
                    fontFamily: '"DM Mono", "Menlo", monospace',
                    fontSize: { xs: '1rem', sm: '1.2rem' },
                    fontWeight: 500,
                    color: '#2D3748',
                    letterSpacing: '0.04em',
                    lineHeight: 1.2,
                  }}
                >
                  {awayTeam?.abbreviation || game.awayTeam}
                </Typography>
              </Box>

              {/* Countdown digits */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 0.25, sm: 0.4 }, flexShrink: 0 }}>
                {countdown.segments.map((seg, i) => (
                  <React.Fragment key={seg.unit}>
                    {i > 0 && <Box sx={{ pt: { xs: 0.5, sm: 0.7 } }}><ColonSeparator blink={!countdown.isImminent} primaryColor={primaryColor} /></Box>}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.4 }}>
                      <DigitCell value={seg.value} isUrgent={countdown.isUrgent} primaryColor={primaryColor} />
                      <Typography
                        sx={{
                          fontSize: '0.5rem',
                          fontWeight: 600,
                          color: '#718096',
                          letterSpacing: '0.12em',
                          lineHeight: 1,
                          textTransform: 'uppercase',
                        }}
                      >
                        {seg.unit}
                      </Typography>
                    </Box>
                  </React.Fragment>
                ))}
              </Box>

              {/* Home team */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 0.75, sm: 1.25 },
                  flex: '0 1 auto',
                  minWidth: 0,
                  flexDirection: 'row-reverse',
                }}
              >
                <TeamLogo mapping={homeTeam} fallbackColor={homeColor} size={44} />
                <Typography
                  noWrap
                  sx={{
                    fontFamily: '"DM Mono", "Menlo", monospace',
                    fontSize: { xs: '1rem', sm: '1.2rem' },
                    fontWeight: 500,
                    color: '#2D3748',
                    letterSpacing: '0.04em',
                    lineHeight: 1.2,
                    textAlign: 'right',
                  }}
                >
                  {homeTeam?.abbreviation || game.homeTeam}
                </Typography>
              </Box>
            </Box>

            {/* Game date + time */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 1.25 }}>
              <ClockIcon sx={{ fontSize: 11, color: '#718096' }} />
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  color: '#718096',
                  letterSpacing: '0.04em',
                }}
              >
                {new Date(game.gameDate).toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </Typography>
            </Box>

            {/* Imminent shimmer bar */}
            {countdown.isImminent && (
              <Box
                sx={{
                  mt: 1.25,
                  height: 2,
                  borderRadius: 1,
                  background: `linear-gradient(90deg, transparent, ${alpha('#f59e0b', 0.4)}, transparent)`,
                  backgroundSize: '200% 100%',
                  animation: `${shimmer} 2s linear infinite`,
                }}
              />
            )}
          </Box>
        )}

        {/* ── Live / Final / Other states ── */}
        {!showCountdown && (
          <>
            {/* Status row */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: hasScore ? 1 : 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ color: '#718096', display: 'flex' }}>
                  {sportIconMap[sport] || <VenueIcon sx={{ fontSize: 16 }} />}
                </Box>
                <Chip
                  label={statusLabel}
                  size="small"
                  sx={{
                    bgcolor: alpha(statusColor, 0.1),
                    color: statusColor,
                    fontWeight: 700,
                    fontSize: '0.65rem',
                    letterSpacing: '0.06em',
                    height: 22,
                    ...(statusPulse && { animation: `${pulse} 2s ease-in-out infinite` }),
                  }}
                  icon={statusPulse ? <DotIcon sx={{ fontSize: '10px !important', color: `${statusColor} !important` }} /> : undefined}
                />
              </Box>
              {game.venue && (
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem', display: { xs: 'none', sm: 'block' } }}>
                  {game.venue}
                </Typography>
              )}
            </Box>

            {/* Score display */}
            {hasScore && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, py: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <TeamLogo mapping={awayTeam} fallbackColor={awayColor} size={36} />
                  <Typography variant="body2" fontWeight={700} noWrap sx={{ fontSize: '1rem', color: '#2D3748' }}>
                    {awayTeam?.abbreviation || game.awayTeam}
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={900}
                    sx={{
                      fontFamily: '"DM Mono", "Menlo", monospace',
                      fontSize: '1.1rem',
                      color: (game.awayScore ?? 0) >= (game.homeScore ?? 0) ? '#2D3748' : '#718096',
                      minWidth: 28,
                      textAlign: 'right',
                    }}
                  >
                    {game.awayScore}
                  </Typography>
                </Box>

                <Typography variant="caption" sx={{ color: '#718096', fontWeight: 600 }}>—</Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    fontWeight={900}
                    sx={{
                      fontFamily: '"DM Mono", "Menlo", monospace',
                      fontSize: '1.1rem',
                      color: (game.homeScore ?? 0) >= (game.awayScore ?? 0) ? '#2D3748' : '#718096',
                      minWidth: 28,
                      textAlign: 'left',
                    }}
                  >
                    {game.homeScore}
                  </Typography>
                  <Typography variant="body2" fontWeight={700} noWrap sx={{ fontSize: '1rem', color: '#2D3748' }}>
                    {homeTeam?.abbreviation || game.homeTeam}
                  </Typography>
                  <TeamLogo mapping={homeTeam} fallbackColor={homeColor} size={36} />
                </Box>
              </Box>
            )}

            {/* Teams display when no score */}
            {!hasScore && !isScheduled && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, py: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <TeamLogo mapping={awayTeam} fallbackColor={awayColor} size={32} />
                  <Typography variant="body2" fontWeight={700} sx={{ fontSize: '1rem', color: '#2D3748' }}>
                    {awayTeam?.abbreviation || game.awayTeam}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: '#718096' }}>vs</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography variant="body2" fontWeight={700} sx={{ fontSize: '1rem', color: '#2D3748' }}>
                    {homeTeam?.abbreviation || game.homeTeam}
                  </Typography>
                  <TeamLogo mapping={homeTeam} fallbackColor={homeColor} size={32} />
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
